/**
 * Application entry point.
 *
 * This file includes `import`'s for all the components/directives used
 * by the HTML page and the controller to provide the configuration.
 */

import './sass/vars_mapbs.scss';
import './sass/vars_desktop.scss';
import './sass/desktop.scss';
import 'gmf/controllers/desktop.scss';
import Image from './images/icons/position.png';

import angular from 'angular';
import olStyleStyle from 'ol/style/Style.js';
import olStyleIcon from 'ol/style/Icon.js';
import { getUid as olUtilGetUid } from 'ol/util';
import olFeature from 'ol/Feature.js';
import olGeomPoint from 'ol/geom/Point.js';
import olCollection from 'ol/Collection.js';
import olSourceVector from 'ol/source/Vector.js';
import olLayerVector from 'ol/layer/Vector.js';
import i18next from 'i18next';
import gmfControllersAbstractDesktopController, {
    AbstractDesktopController,
} from 'gmf/controllers/AbstractDesktopController.js';
import gmfGooglestreetviewModule from 'ngeo/streetview/module';
import ngeoQueryModeSelector from 'ngeo/query/ModeSelector';
import ngeoQueryMode from 'ngeo/query/Mode';
import ngeoQueryAction from 'ngeo/query/Action';
import { PermalinkParam } from 'gmf/index';
import { findGroupByName, getFlatNodes, findObjectByName } from 'gmf/theme/Themes';
import { PermalinkService } from 'gmf/permalink/Permalink';

import mapbsAbout from 'mapbs/components/about/about';
import mapbsApplications from 'mapbs/components/applications/applications';
import mapbsCitymodel3d from 'mapbs/components/citymodel3d/citymodelpanel';
import mapbsCitymodel3dService from 'mapbs/components/citymodel3d/citymodel3dservice/citymodel3dservice';

import mapbsContextMenu from 'mapbs/components/contextmenu/contextmenu';
import mapbsContextualdataLink from 'mapbs/components/contextualdatalink/contextualdatalink';
import mapbsCopyToClip from 'mapbs/components/copytoclip/copytoclip';
import mapbsEditlink from 'mapbs/components/editlink/editlink';
import mapbsRealestateinformationdesktop from 'mapbs/components/realestateinformation/desktop/realestateinformationdesktop';
import mapbsReportpanel from 'mapbs/components/reportpanel/reportpanel';
import mapbsInfra3d from 'mapbs/components/infra3d/infra3dComponent';
import mapbsShare from 'mapbs/components/share/shareComponent';
import mapbsSearchtip from 'mapbs/components/searchtip/searchtip';
import mapbsSearchtipcontent from 'mapbs/components/searchtip/searchtipcontent';
import mapbsLayertreeCustomization from 'mapbs/components/layertree/mapbsLayertreeCustomization';
import displayquerywindow from 'mapbs/components/displayquerywindow/displayquerywindow';
import AuthenticationService, { RouteSuffix } from 'ngeo/auth/service';
import mapbsFeedback from 'mapbs/components/feedback/feedback.js';
import mapbsHelp from 'mapbs/components/help/help.js';
import KeycloakBS from './keycloakbs.js';
import * as olProj from 'ol/proj.js';

import 'mapbs/components/authentication/authentication';

if (!window.requestAnimationFrame) {
    alert(
        'Your browser is not supported, please update it or use another one. You will be redirected.\n\n' +
        'Votre navigateur n\'est pas supporté, veuillez le mettre à jour ou en utiliser un autre. ' +
        'Vous allez être redirigé.\n\n' +
        'Ihr Browser wird nicht unterstützt, bitte aktualisieren Sie ihn oder verwenden Sie einen anderen. ' +
        'Sie werden weitergeleitet.'
    );
    window.location.href = 'https://www.geo.bs.ch/';
}

const keycloakBS = new KeycloakBS();

// Autologin with Keycloak
AuthenticationService.load_ = function (initMode = 'check-sso') {
    const urlParams = new URLSearchParams(window.location.search);
    const sso = urlParams.get('sso');
    if (sso !== null) {
        // sso enabled. => Force Login with AD
        keycloakBS.enableSso();
        initMode = 'login-required';
        // Remove sso parameter immediately, because we do not want it to appear if the link is shared
        keycloakBS.deleteUrlParam('sso');
    }

    function standardGmfLogin(gmfLogging) {
        if (gmfLogging) {
            // Standard GMF login

            const url = `${this.baseUrl_}/${RouteSuffix.IS_LOGGED_IN}`;
            const options = { method: 'GET', credentials: 'include' };
            fetch(url, options)
                .then((resp) => resp.json())
                .then((data) => this.checkUser_(data))
                .then(
                    (data) => this.handleLogin_(true, data),
                    () => {
                        throw new Error('Login fail.');
                    }
                );
        }
    }

    function success(gmfLogging) {
        // Set Keycloak cookie only if token is defined
        if (keycloakBS.hasToken()) {
            const url = `${this.baseUrl_}/KeycloakLogin`;
            fetch(url, {
                credentials: 'include',
                headers: { 'Authorization': `Bearer ${keycloakBS.getToken()}` },
            })
                .then(() => {
                    standardGmfLogin.bind(this)(gmfLogging);
                })
                .catch(console.log('Server-Token could not be refreshed.'));
        } else {
            // Not Token: Log out from GMF
            const url = `${this.baseUrl_}/KeycloakLogout`;
            fetch(url).then(() => {
                standardGmfLogin.bind(this)(gmfLogging);
            });
        }
    }

    if (keycloakBS.didInitialize()) {
        keycloakBS.login();
    }
    else {
        keycloakBS.keycloak
            .init({ onLoad: initMode })
            .then(() => {
                success.bind(this)(true);
            })
            .catch(function (e) {
                console.warn('Failed to get token: ' + e);
            });
    }

    keycloakBS.keycloak.onTokenExpired = () => {
        keycloakBS.keycloak
            .updateToken(30)
            .then(() => {
                success.bind(this)(false);
            })
            .catch(() => {
                console.log('Failed to refresh token');
            });
    };
};

// Logout with Keycloak
AuthenticationService.logout = function () {
    // Desable SSO, otherwise the login will be made automatically again after the logout
    keycloakBS.disableSso();

    // Remember the current URL: It this is a private layer in the URL, we want the warning message to be displayed
    this.initialUri = new URL(document.documentURI);

    keycloakBS.deleteUrlParam('baselayer_ref');

    // Log out from GMF
    const url = `${this.baseUrl_}/KeycloakLogout`;
    fetch(url)
        .then(() => {
            // Logout from Keycloak
            keycloakBS.logout();
        })
        .catch(console.log('Server-Token could not be deleted.'));
};

// Login with Keycloak
AuthenticationService.login = function () {
    keycloakBS.deleteUrlParam('baselayer_ref');
    AuthenticationService.load_('login-required');
};

PermalinkService.prototype.setNodeTime_ = function (treeCtrl) {

    const time = this.ngeoStateManager_.getInitialStringValue(`tree_time_${treeCtrl.node.name}`);
    if (time) {
        const bounds = time.split('/');
        const node = /** @type {import('gmf/themes').GmfGroup|!import('gmf/themes').GmfLayerWMS} */ (
            treeCtrl.node
        );
        node.time.minDefValue = bounds[0];
        node.time.maxDefValue = bounds[1];
        const dataSource = this.gmfDataSourcesManager_.getDatasource(olUtilGetUid(treeCtrl.node));

        if (dataSource) {
            if (!(dataSource instanceof gmfDatasourceOGC)) { // eslint-disable-line no-undef
                throw new Error('Wrong dataSource type');
            }
            dataSource.timeLowerValue = new Date(bounds[0]).getTime();
            dataSource.timeUpperValue = new Date(bounds[1]).getTime();
            //** BS custom code start */
            dataSource.dimensionsConfig['TIME'] = time;
            //** BS customc code end */
        } else if (treeCtrl.children) {
            treeCtrl.children.forEach((child) => {
                const dataSource = this.gmfDataSourcesManager_.getDatasource(olUtilGetUid(child.node));
                if (dataSource) {
                    dataSource.timeLowerValue = new Date(bounds[0]).getTime();
                    dataSource.timeUpperValue = new Date(bounds[1]).getTime();
                    //** BS customc code start */
                    dataSource.dimensionsConfig['TIME'] = time;
                    //** BS customc code end */
                }
            });
        }
    }
};

/**
 * @private
 */
class Controller extends AbstractDesktopController {
    /**
   * @param {angular.IScope} $scope Scope.
   * @param {angular.auto.IInjectorService} $injector Main injector.
   * @param {angular.$http} $http the angular http service
   * @param {angular.$window} $window the window
   * @param {import('ngeo/query/ModeSelector').QueryModeSelector} ngeoQueryModeSelector The ngeo QueryModeSelector service
   * @param {import('ngeo/misc/AutoProjection').AutoProjectionService} ngeoAutoProjection The ngeo coordinates service.
   * @param {mapbs/component/citymodel3d/citymodel3dservice/citymodel3dservice} mapbsCitymodel3dService
   * @ngInject
   */
    constructor($scope, $injector, $http, $window, ngeoQueryModeSelector, ngeoAutoProjection,mapbsCitymodel3dService) {
        super($scope, $injector);

        /**
         * @type {angular.IScope}
         * @private
         */
        this.scope = $scope;

        /**
         * @type {angular.window}
         * @private
         */
        this.window = $window;

        /**
         * @type {angular.$http}
         * @private
         */
        this.http = $http;

        /**
         * @param {angular.auto.IInjectorService}
         * @private
         */
        this.$injector = $injector;

        /**
         * @type {import('ngeo/query/ModeSelector').QueryModeSelector}
         */
        this.ngeoQueryModeSelector = ngeoQueryModeSelector;

        /**
         * @type {import('ngeo/misc/AutoProjection').AutoProjectionService}
         * @private
         */
        this.ngeoAutoProjection = ngeoAutoProjection;

        /**
         * @type {string}
         */
        this.environmentName = this.$injector.get('environmentName');

        /**
         * @type {!Array<number>}
         */
        this.scaleSelectorValues = this.$injector.get('ngeoScaleSelectorOptions');

        /**
         * @type {?string}
         */
        this.displaywindowWidth = '32rem';

        /**
         * @type {?string}
         */
        this.displaywindowHeight = '20rem';

        /**
         * @type {Array<string>}
         */
        this.elevationLayers = ['profile_gelaende'];

        /**
         * @type {Object<string, import('gmf/mobile/measure/pointComponent.js').LayerConfig>}
         */
        this.elevationLayersConfig = {};

        /**
         * @type {string}
         */
        this.selectedElevationLayer = this.elevationLayers[0];

        /**
         * @type {Object<string, import('gmf/profile/component.js').ProfileLineConfiguration>}
         */
        this.profileLinesconfiguration = {
            'profile_gelaende': { color: '#246036' },
        };

        /**
         * @type {boolean}
         */
        this.reportPanelActive = false;


        /**
         * @type {boolean}
         */
        this.helpPanelActive = false;

        /**
         * @type {string}
         */
        this.reportPanelStateActive = 'inactive';

        /**
         * @type {boolean}
         */
        this.realestateinformationDesktopActive = false;

        /**
         * @type {boolean}
         */
        this.citymodel3dActive = false;

        /**
         * @type {boolean}
         */
        this.contextmenuActive = false;

        /**
         * @type {boolean}
         */
        this.modalFeedbackCompShown = false;

        /**
         * @type {boolean}
         */

        this.modalFeedbackShown = false;

        /**
         * @type {string}
         */
        this.pythonWebserverUrl;

        this.toolbarDiv = null;

        /**
         * @type {string}
         */
        this.geodatashopUrl = this.$injector.get('geoshop_url');

        /**
         * @type {string}
         */
        this.initialUri = new URL(document.documentURI);

        /** @type {mapbs/component/citymodel3d/citymodel3dservice/citymodel3dservice} */
        this._citymodel3dService = mapbsCitymodel3dService;

        /**
         * @type {boolean}
         */
        this.displayAccessRightsMessage = false;

        /**
         * @type {!olstyleStyle}
         * @export
         */
        this.googleStreetViewStyle = new olStyleStyle({
            image: new olStyleIcon({
                anchor: [0.5, 1],
                src: Image,
                scale: 0.5,
            }),
        });


        /**
        * @type {string}
        */
        this.mapbsServiceUserKeycloak = this.$injector.get('mapbs_service_user_keycloak');


        /**
         * @type {boolean}
         */
        this.isMapbsAnonymousUser = true;

        /**
         * @type {string}
         */
        this.loginAction = 'Anmelden';

        /**
         * Load Icons
         * @type {Object.<string, string>}
         * @private
         */
        this.imgList = this.importAll(
            require.context('./images/icons/', false, /\.(png)$/)
        );

        // add feature icons to source vector for pin overlay
        /**
         * @type {olCollection}
         * @const
         * @protected
         */
        this.featureIcons_ = new olCollection();
        /**
         * @type {olSourceVector}
         * @const
         * @private
         */
        this.vectorSource_ = new olSourceVector({
            features: this.featureIcons_,
        });

        /**
         * @type {olLayerVector}
         * @const
         * @private
         */
        this.vectorLayer_ = new olLayerVector({
            source: this.vectorSource_,
        });

        // add layer to map
        this.map.addLayer(this.vectorLayer_);


        // Allow angular-gettext-tools to collect the strings to translate
        /**
         * @type {angular.gettext.gettextCatalog}
         */

        this.$scope.$watch(
            () => this['lang'],
            (language) => this.languageChanged_(language)
        );

        this.$scope.$watch(
            () => this.gmfUser.username,
            () => {
                this.urlString = $window.location.search;
                this.loadApplicationSetting_();
                if (this.gmfUser.username !== null) {
                    // When a user is logged into the application, the selected objects will be displayed in the grid panel, and not in the query-window.
                    // Therefore, we need to refresh the permalink here, otherwise the query-window will just disapear, and the grid panel will never be opened.
                    const wfsPermalinkData = this.permalink_.getWfsPermalinkData_();
                    if (wfsPermalinkData !== null && this.permalink_.ngeoWfsPermalink_) {
                        this.permalink_.ngeoWfsPermalink_.issue(wfsPermalinkData, this.map);
                    }
                    this.isMapbsAnonymousUser = this.gmfUser.username === this.mapbsServiceUserKeycloak;
                    this.loginAction = this.isMapbsAnonymousUser ? 'Anmelden' : 'Abmelden';
                }
            }
        );

        this.$scope.$watch(
            () => this.gmfThemes.loaded,
            () => {
                this.gmfThemes.getThemesObject().then(themes => {
                    const reqAuth = this.requireAuthentication(themes);
                    if (reqAuth) {
                        this.displayAccessRightsMessage = true;
                    }
                });
            }
        );

        this.urlString = $window.location.search;
        // Listens for window resize
        const window = angular.element($window);
        window.bind('resize', () => this.toolbarsize());

        // The zoom levels of MapBS and MapBS-3D are not the same.
        // Plexmap ist using OSM Zoom Levels: https://wiki.openstreetmap.org/wiki/Zoom_levels
        // While MapBS ist using the same as Swisstopo : https://git.geo.intranet.bs.ch/GDM/MapBS/wmts-server
        // Therefore, we have to do a conversion
        const zoom3dParamName = 'mapbs3d_zoom';
        if (this.ngeoLocation.hasParam(zoom3dParamName)) {
            const zoomLevel3d = this.ngeoLocation.getParamAsInt(zoom3dParamName);
            const zoomLevel = this.getZoomLevelFrom3D(zoomLevel3d);
            this.ngeoLocation.updateParams({ 'map_zoom': zoomLevel });
            this.ngeoLocation.deleteParam(zoom3dParamName);
            const view = this.map.getView();
            view.setZoom(zoomLevel);
        }

        /**
         * @type {string}
         */
        this.configUrl = this.$injector.get('ngeoconfigUrl');
        this.baseUrl = `${$injector.get('baseUrl')}`;
        this.loadApplicationSetting_();
        this.initializeComponents_();

        const recenterIcon = this.ngeoLocation.getParam('recenter_icon');
        const mapX = this.ngeoLocation.getParam('map_x');
        const mapY = this.ngeoLocation.getParam('map_y');
        if (recenterIcon && mapX && mapY) {
            this.addFeatureIcon(
                recenterIcon,
                /** @type {olCoordinate} */([mapX, mapY])
            );
        }

        this.ngeoAutoProjection.stringToCoordinates = function (str) {
            const coords = /\b(\d+(?:\.\d+)?)[\s,/;]+(\d+(?:\.\d+)?)\b/.exec(str);
            if (coords) {
                const x = parseFloat(coords[1].replace(/'/g, ''));
                const y = parseFloat(coords[2].replace(/'/g, ''));
                if (!isNaN(x) && !isNaN(y)) {
                    return [x, y];
                }
            }
            return null;
        };

        /**
         * @type {boolean}
         */
        this.consentCookie = !!document.cookie.split('; ').find((row) => row.startsWith('mtm_consent='));

        this.enableTracking = () => {
            $window._paq.push(['rememberConsentGiven']);
        };

        this.disableTracking = () => {
            document.cookie = 'mtm_consent=0;max-age=31536000';
        };

        this.enableTrackingFor1Day = () => {
            $window._paq.push(['rememberConsentGiven', 24]);
        };

    }

    /**
   * @private
   */
    getZoomLevelFrom3D(zoomLevel3d) {
        // An exact mapping cannot be calculated, because the scales do not correspond between the tile matrixes.
        // Therefore, this mapping has been defined arbitrarily after observing the behavior of the 2 applications
        const zoomLevelMapping = [
            0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 2, 3, 4, 6, 7, 8, 9, 10, 10, 11,
        ];
        return zoomLevelMapping[zoomLevel3d];
    }

    loginWithInitialUri() {
        // Overwrite default keycloak login to add redirectUri parameter
        keycloakBS.RedirectUri = this.initialUri.href;
        AuthenticationService.login();
    }

    /**
   * @private
   */
    languageChanged_(language) {
        this.aboutAdresslist = this.aboutAdresslistAllLanguages_[language];
        this.applicationLinks = this.applicationLinksAllLanguages_[language];
        this.searchtipData = this.searchtipDataAllLanguages_[language];
        i18next.changeLanguage(language);
    }

    /**
   * @private
   */
    loadApplicationSetting_() {
        this.getAboutAdresslist_();
        this.getApplicationLinks_();
        this.getSearchtipdata_();
        if (this.urlString.includes('grundstuecksinfo=true')) {
            this.realestateinformationDesktopActive = true;
        }
    }

    /**
   * @private
   */
    initializeComponents_() {
        this.initializeAbout_();
        this.initializeApplications_();
        this.initializeEditlink_();
        this.initializeInfra3d_();
        this.initializeSearchTip_();
        this.initalizeReleasebanner_();
    }

    requireAuthentication(themes) {
        const treeGroupNames = this.initialUri.searchParams.get(PermalinkParam.TREE_GROUPS);
        let authenticationRequired = false;
        if (treeGroupNames !== null && treeGroupNames !== undefined) {
            const groupsNames = treeGroupNames.split(',');
            for (let i = 0; i < groupsNames.length; i++) {
                const groupName = groupsNames[i];
                const group = findGroupByName(themes, groupName);
                if (!group) {
                    authenticationRequired = true;
                }
                else {
                    // Check layers
                    if (this.requireAuthenticationForLayer(groupName, group)) {
                        authenticationRequired = true;
                    }
                }
            }
        }

        return authenticationRequired;
    }

    requireAuthenticationForLayer(groupName, group) {
        const internalNodes = [];
        getFlatNodes(group, internalNodes);
        const treeGroupLayersNames = this.initialUri.searchParams.get(`tree_group_layers_${groupName}`);
        if (treeGroupLayersNames !== null && treeGroupLayersNames !== undefined && treeGroupLayersNames.length !== 0) {
            const layersNames = treeGroupLayersNames.split(',');
            for (let i = 0; i < layersNames.length; i++) {
                const layerName = layersNames[i];
                if (!findObjectByName(internalNodes, layerName)) {
                    return true;
                }
            }
        }

        return false;
    }

    /**
   * Import all Images/Cons path
   */
    importAll(r) {
        const images = {};
        r.keys().map((item) => {
            images[item.replace('./', '')] = r(item);
        });
        return images;
    }

    /**
   * @private
   */
    setTooltip() {
        $('.bs-tooltip').tooltip({ boundary: 'window', trigger: 'hover' });
    }

    /**
   * @private
   */
    getAboutAdresslist_() {
        this.http.get(`${this.configUrl}/aboutAdresslist`).then((response) => {
            this.aboutAdresslistAllLanguages_ = response.data;
            this.aboutAdresslist = this.aboutAdresslistAllLanguages_[this.lang];
        });
    }

    /**
   * @private
   */
    getSearchtipdata_() {
        this.http.get(`${this.configUrl}/searchtipData`).then((response) => {
            this.searchtipDataAllLanguages_ = response.data;
            this.searchtipData = this.searchtipDataAllLanguages_[this.lang];
        });
    }

    /**
   * @private
   */
    getApplicationLinks_() {
        this.http.get(`${this.configUrl}/applicationLinks`).then((response) => {
            this.applicationLinksAllLanguages_ = response.data;
            this.applicationLinks = this.applicationLinksAllLanguages_[this.lang];
        });
    }

    /**
   * @private
   */
    initializeAbout_() {
        /**
         * @type {boolean}
         */
        this.modalAboutShown = false;

        /**
     * @type {Object<string, Address[]>}
     * @private
     */
        this.aboutAdresslistAllLanguages_ = {};

        /**
     * @type {Address[]}
     */
        this.aboutAdresslist = this.aboutAdresslistAllLanguages_[this.lang];
    }

    /**
   * @private
   */
    initializeSearchTip_() {
        /**
         * @type {Object.<string, Array.<SearchTipItem>>}
         * @private
         */
        this.searchtipDataAllLanguages_ = {};

        /**
     * @type {Array.<SearchTipItem>}
     */
        this.searchtipData = this.searchtipDataAllLanguages_[this.lang];
    }

    /**
   * @private
   */
    initalizeReleasebanner_() {
        this.http.get(`${this.baseUrl}releasebanner`).then((response) => {
            this.releasemessage = response.data;
            document.getElementById('releasemessage-text').innerHTML = response.data.text;
            this.showReleasemessage = true;
        });
    }


    /**
   * @private
   */
    initializeApplications_() {
        /**
         * @type {Object<string, Array<mapbs.ApplicationLinkData>>}
         * @private
         */
        this.applicationLinksAllLanguages_ = {};

        /**
     * @type {Array<mapbs.ApplicationLinkData>}
     */
        this.applicationLinks = this.applicationLinksAllLanguages_[this.lang];

        /**
     * @type {boolean}
     */
        this.applicationsPanelActive = false;
        this.$scope.$watch(
            () => this.applicationsPanelActive,
            (active) => {
                if (!active) {
                    this.$scope.$broadcast('open_link', '');
                }
            }
        );
    }

    /**
   *@private
   */
    initializeEditlink_() {
        /**
         * @type {boolean}
         */
        this.editlinkActive = false;

        /**
     * @type {string}
     * @private
     */
        const mapbsEditUrl = this.$injector.get('editUrl');

        /**
     * @type {Array<object>}
     */
        this.datenerfassungsebenen = [
            {
                name: 'Baumkataster',
                instance: `${mapbsEditUrl}/stg`,
                functionality: 'plugin_ba_datenerfassung',
                urlextension:
                    'tree_group_layers_Datenerfassung%20Baumkataster=BA_Baumkataster_baum&tree_groups=Datenerfassung%20Baumkataster',
            },
            {
                name: 'Demo',
                instance: `${mapbsEditUrl}/edit`,
                functionality: 'plugin_demo_datenerfassung',
                urlextension:
                    'tree_group_layers_Datenerfassung%20Demo=Demo_Flaeche%2CDemo_Line%2CDemo_Point&tree_groups=Datenerfassung%20Demo',
            },
            {
                name: 'Energieflussschemas',
                instance: `${mapbsEditUrl}/edit`,
                functionality: 'plugin_ef_datenerfassung',
                urlextension:
                    'tree_group_layers_Datenerfassung%20Energieflussschemas=EF_Stromflussschema_Stromverbindung_digi,EF_Stromflussschema_Stromzaehler_digi,EF_Wasserflussschema_Wasserverbindung_digi,EF_Wasserflussschema_Wasserzaehler_digi,EF_Waermeflussschema_Heizverbindung_digi,EF_Waermeflussschema_Heizzaehler_digi&tree_groups=Datenerfassung%20Energieflussschemas',
            },
            {
                name: 'Entsorgungsstellen',
                instance: `${mapbsEditUrl}/edit`,
                functionality: 'plugin_es_datenerfassung',
                urlextension:
                    'tree_group_layers_Datenerfassung%20Entsorgungsstellen=ES_Verkaufsstellen_VerkaufVignetteRiehenBettingen_digi%2CES_Verkaufsstellen_VerkaufVignetteBasel_digi%2CES_Verkaufsstellen_VerkaufChipkarteBioKlappeBasel_digi%2CES_Entsorgungsstellen_Tierkadaver_digi%2CES_Entsorgungsstellen_Pneus_digi%2CES_Entsorgungsstellen_MunitionUndWaffen_digi%2CES_Entsorgungsstellen_Korkzapfen_digi%2CES_Entsorgungsstellen_Kleidercontainer_digi%2CES_Entsorgungsstellen_KleiderUndTextilien_digi%2CES_Entsorgungsstellen_Druckertankstelle_digi%2CES_Entsorgungsstellen_Brillen_digi%2CES_Entsorgungsstellen_BrauchbareDinge_digi%2CES_Entsorgungsstellen_AnnahmestelleSonderabfall_digi%2CES_Entsorgungsstellen_Recyclingstation_digi%2CES_Entsorgungsstellen_Recyclingpark_digi%2CES_Entsorgungsstellen_Kehrichtverbrennung_digi%2CES_Entsorgungsstellen_BioKlappeBasel_digi&tree_groups=Datenerfassung%20Entsorgungsstellen',
            },
            {
                name: 'Gastro-Sekundärlärm',
                instance: `${mapbsEditUrl}/edit`,
                functionality: 'plugin_gl_datenerfassung',
                urlextension:
                    'tree_group_layers_Datenerfassung%20Gastro-Sekundärlärm=GL_AbschnittsStoergrade_digi&tree_groups=Datenerfassung%20Gastro-Sekundärlärm',
            },
            {
                name: 'Hochstammobstbäume',
                instance: `${mapbsEditUrl}/edit`,
                functionality: 'plugin_ho_datenerfassung',
                urlextension:
                    'tree_group_layers_Datenerfassung%20Hochstammobstbäume=HO_Hochstammobstbaeume_digi&tree_groups=Datenerfassung%20Hochstammobstbäume',
            },
            {
                name: 'Kleinbauten Riehen',
                instance: `${mapbsEditUrl}/edit`,
                functionality: 'plugin_kb_datenerfassunng',
                urlextension:
                    'tree_group_layers_Datenerfassung%20Kleinbauten%20Riehen=KB_KleinbautenRiehen_Linie_digi,KB_KleinbautenRiehen_Flaeche_digi&tree_groups=Datenerfassung%20Kleinbauten%20Riehen',
            },
            {
                name: 'Leitbild Strassenbäume',
                instance: `${mapbsEditUrl}/edit`,
                functionality: 'plugin_lbs_datenerfassung',
                urlextension:
                    'tree_group_layers_Datenerfassung%20Leitbild%20Strassenbäume=LS_Baumkataster%2CLS_LeitbildStrassenbaeume_digi&tree_groups=Datenerfassung%20Leitbild%20Strassenbäume&map_zoom=6',
            },
            {
                name: 'Schulstandorte (Gemeinden Riehen & Bettingen)',
                instance: `${mapbsEditUrl}/edit`,
                functionality: 'plugin_so_datenerfassung',
                urlextension:
                    '&tree_group_layers_Datenerfassung%20Schulstandorte%20(Gemeinden%20Riehen%20%26%20Bettingen)=SO_Schulstandort_digi&tree_groups=Datenerfassung%20Schulstandorte%20(Gemeinden%20Riehen%20%26%20Bettingen)&map_x=2615860&map_y=1269472',
            },
        ];
    }


    /**
   * @private
   */
    initializeInfra3d_() {
        /**
         * @type {boolean}
         */
        this.infra3DActive = false;

        /**
     * @type {string}
     */
        this.infra3DUrl = this.$injector.get('infra3d_url');
    }

    getPluginAuthorization(functionality) {
        const user = this.gmfUser;
        if (
            user === undefined ||
            user['functionalities'] === null ||
            user['functionalities']['authorized_plugins'] === undefined
        ) {
            return false;
        }
        this.setTooltip();
        return (
            user['functionalities']['authorized_plugins'].indexOf(functionality) !==
            -1
        );
    }

    searchActionCallback(action) {
        const actionName = action['action'];
        const data = action['data'];

        if (actionName === 'open_tool') {
            switch (data) {
                case 'grundstuckinformation':
                    this.realestateinformationDesktopActive = true;
                    break;
                case 'erdbeben':
                    this.reportPanelActive = true;
                    this.$scope.$broadcast(actionName, 'erdbeben');
                    break;
                case '3D':
                    this.citymodel3dActive = true;
                    this.$scope.$broadcast(actionName, '3D');
                    break;
            }
        }

        if (actionName === 'open_link') {
            this.$scope.$broadcast(actionName, data);
            this.applicationsPanelActive = true;
        }
    }

    /**
   * @private
   */
    toolbarsize() {
        if (this.toolbarDiv === null) {
            // Initialize only once
            this.toolbarDiv = document.getElementsByClassName('gmf-app-bar')[0];
        }
        const hasVerticalScrollbar =
            this.toolbarDiv.scrollHeight > this.toolbarDiv.clientHeight;
        if (hasVerticalScrollbar) {
            this.toolbarDiv.style.width = '3.75rem';
        } else {
            this.toolbarDiv.style.width = '3.125rem';
        }
    }

    showToolbar() {
        // This is a small trick to raise a toolbar resize event
        // When the user log in/out.
        // This is not a very elegant solution, but it does the job without the need to ask for a change in ngeo
        this.toolbarsize();
        // Toolbar is always visible
        return true;
    }

    zoomIn() {
        if (this.map.getView().getResolution() > this.map.getView().minResolution_) {
            this.zoom(1);
        }
    }

    zoomOut() {
        if (this.map.getView().getResolution() < this.map.getView().maxResolution_) {
            this.zoom(-1);
        }
    }
    open3DMapInNewTab() {
        const coord = olProj.transform(this.map.getView().getCenter(), 'EPSG:2056', 'EPSG:4326');
        this._citymodel3dService.getUrl(coord[0], coord[1], this.scaleSelectorValues, null).then(url => {
            window.open(url, '3d map');
        });
    }

    /**
   * @private
   */
    zoom(step) {
        const view = this.map.getView();
        const currentResolution = view.getResolution();
        if (currentResolution) {
            const newResolution = view.resolutions_[view.resolutions_.findIndex(x => x === currentResolution) + step];
            view.setResolution(newResolution);
        }
    }

    logging(topic, action, isOpen) {
        if (isOpen) {
            this.http.get(`${this.baseUrl}logging?topic=${topic}&action=${action}`);
        }
    }

    resetSelectionMode() {
        this.ngeoQueryModeSelector.mode = ngeoQueryMode.CLICK;
        this.ngeoQueryModeSelector.action = ngeoQueryAction.REPLACE;
    }

    openGeodatashopUrl() {
        window.open(this.geodatashopUrl, '_blank');
    }

    /**
   * @param {string} icon Icon name.
   * @param {olCoordinate} coordinate Coordinate of icon.
   * @private
   */
    addFeatureIcon(icon, coordinate) {
        const iconFullPath = this.imgList[icon];
        const feature = new olFeature({
            name: icon,
            geometry: new olGeomPoint(coordinate),
        });
        feature.setStyle(
            new olStyleStyle({
                image: new olStyleIcon({
                    src: iconFullPath, // set image path to parameter value
                    anchor: [0.5, 1],
                    scale: 0.5,
                }),
            })
        );
        this.featureIcons_.push(feature);
    }
}

/**
 * @private
 */
const formatCoordinates = function () {
    const formatFn = function (coordinate) {
        if (
            coordinate !== undefined &&
            coordinate.length === 2 &&
            typeof coordinate[0] === 'number' &&
            typeof coordinate[1] === 'number'
        ) {
            const x = (Math.round(coordinate[0] * 10) / 10)
                .toString()
                .replace('\'', '');
            const y = (Math.round(coordinate[1] * 10) / 10)
                .toString()
                .replace('\'', '');

            const formatedCoords = `${x} / ${y}`;
            return formatedCoords;
        }
    };

    return formatFn;
};

/**
 * @hidden
 */
const moduleDesktop = angular.module('Appdesktop', [
    gmfControllersAbstractDesktopController.name,
    gmfGooglestreetviewModule.name,
    mapbsLayertreeCustomization.name,
    displayquerywindow.name,
    mapbsAbout.name,
    mapbsApplications.name,
    mapbsCitymodel3d.name,
    mapbsCitymodel3dService.name,
    mapbsCopyToClip.name,
    mapbsContextMenu.name,
    mapbsContextualdataLink.name,
    mapbsEditlink.name,
    mapbsReportpanel.name,
    mapbsInfra3d.name,
    mapbsSearchtip.name,
    mapbsSearchtipcontent.name,
    mapbsFeedback.name,
    ngeoQueryModeSelector.name,
    mapbsRealestateinformationdesktop.name,
    mapbsHelp.name,
    mapbsShare.name
]);

moduleDesktop.value('gmfContextualdatacontentTemplateUrl', 'gmf/contextualdata');
moduleDesktop.run(
    /* @ngInject */($templateCache) => {
        // @ts-ignore: webpack
        $templateCache.put(
            'gmf/contextualdata',
            require('mapbs/components/contextualdata/contextualdata.html')
        );
        $templateCache.put(
            'gmf/layertree',
            require('mapbs/components/layertree/layertree.html')
        );
        $templateCache.put(
            'gmf/import/importdatasourceComponent',
            require('mapbs/components/importdatasource/importdatasource.html')
        );
        $templateCache.put(
            'gmf/query/windowComponent',
            require('mapbs/components/displayquerywindow/displayquerywindow.html')
        );

    }
);

moduleDesktop.constant('gmfTreeManagerModeFlush', false);

moduleDesktop.constant('ngeoGoogleStreetviewOptions', {
    'viewer': 'google',
});

moduleDesktop.value('gmfLayertreeOptions', {
    'legendIcon': { 'width': 'auto', 'height': 'auto' },
});

moduleDesktop.controller('DesktopController', Controller);

moduleDesktop.filter('mapbsFormatCoordinates', formatCoordinates);

export default moduleDesktop;
